import type { Context } from "@onesolution/middlewares";
import {
	type DefinedInitialDataInfiniteOptions,
	type DefinedInitialDataOptions,
	type InfiniteData,
	type MutationFunction,
	type UseInfiniteQueryResult,
	type UseMutationOptions,
	type UseQueryResult,
	infiniteQueryOptions,
	queryOptions,
	useInfiniteQuery,
	useMutation,
	useQuery,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { v7 as uuidv7 } from "uuid";
// Auth
export * from "./auth/handleAuthCallback";
export * from "./auth/login";
export * from "./auth/logout";

// App Monday fetches
export * from "./getClientInfos";
export * from "./getItemPageByValues";
export * from "./operations";
export * from "./tasks/getTasks";

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
type AnyFn = (...args: any) => any;

export function wrapWithUseQuery<
	T extends AnyFn,
	PARAM = Parameters<T>[0]["data"],
>(
	key: string,
	base: T,
	defaultOpts?: Partial<DefinedInitialDataOptions<ReturnType<T>>> &
		Partial<DefinedInitialDataInfiniteOptions<ReturnType<T>>>,
) {
	return Object.assign(base, {
		useQuery: (
			params?: PARAM,
			opts?: Partial<DefinedInitialDataOptions<ReturnType<T>>>,
		) => {
			return useQuery(
				queryOptions<ReturnType<T>>({
					retry: false,
					queryKey: [key, params],
					queryFn: (() =>
						base({ data: params || ({} as PARAM) })) as () => ReturnType<T>,
					...defaultOpts,
					...opts,
				}),
			) as UseQueryResult<Awaited<ReturnType<T>>>;
		},

		useInfiniteQuery: (
			params?: PARAM,
			opts?: Partial<DefinedInitialDataInfiniteOptions<ReturnType<T>>>,
		) => {
			const out = useInfiniteQuery(
				infiniteQueryOptions({
					queryKey: ["infinite", key, params],
					retry: false,
					queryFn: async ({ pageParam }) =>
						base({
							data: { cursor: pageParam, limit: 20, ...(params || {}) },
						}),
					initialPageParam: undefined as string | undefined,
					getNextPageParam: (lastPage) => lastPage.cursor,
					...defaultOpts,
					...opts,
				}),
			);

			const data = useMemo(
				() => ({
					items: out.data?.pages.flatMap((page) => page.items) || [],
					cursor: out.data?.pages[out.data?.pages.length - 1]?.cursor,
				}),
				[out.data?.pages],
			);

			return Object.assign(out, {
				data,
			}) as unknown as Omit<
				UseInfiniteQueryResult<InfiniteData<ReturnType<T>, unknown>, Error>,
				"data"
			> & { data: Awaited<ReturnType<T>> };
		},
	});
}

export function wrapWithUseMutation<
	T extends AnyFn,
	Params = Parameters<T>[0]["data"],
>(
	base: T,
	defaultOpts?: Partial<
		UseMutationOptions<Awaited<ReturnType<T>>, Error, Params, Context>
	>,
) {
	const key = uuidv7();

	return Object.assign(base, {
		useMutation: (
			opts?: Partial<
				UseMutationOptions<Awaited<ReturnType<T>>, Error, Params, Context>
			>,
		) => {
			return useMutation({
				retry: false,
				mutationKey: [key],
				mutationFn: ((data, ...args) =>
					base({ data }, ...args)) as MutationFunction<
					Awaited<ReturnType<T>>,
					Params
				>,
				...defaultOpts,
				...opts,
			});
		},
	});
}
